import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Link2 } from 'lucide-react';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui';
import type { IqServiceInventoryItem } from '@/types/iq-service-inventory';

/**
 * Nutzungs-Badge fuer eine Inventar-Kennung (Spec 347 §6, Dimension a).
 *
 * ⚠ Verbindliche Abweichung vom Asset-`UsageBadge`: das Badge rendert
 * `fallbackUsageCount` (Eintrags-Zaehlung), NICHT `usedBy.length`. `usedBy` (distinkte
 * Services) speist NUR den Tooltip / das Drill-down. Ein Service, der dieselbe Kennung in
 * zwei FallbackChain-Slots fuehrt, zaehlt im Badge zweimal, im Tooltip einmal.
 *
 * Drei Zustaende: nicht ermittelbar (degradiert) / ungenutzt (0) / "in N Eintraegen".
 */
export function IqServiceUsageBadge({ item }: { item: IqServiceInventoryItem }) {
  const { t } = useTranslation('iq-service-inventory');

  // Degradation (Spec §Degradation + 352 §6): Nutzung nicht ermittelbar (Liste down)
  // ODER unzuverlaessig (Seiten-Schranke erreicht) → kein "in 0", kein toter Link.
  if (item.usageUnavailable || item.usageTruncated) {
    return (
      <Badge variant="outline" className="shrink-0 gap-1 text-muted-foreground">
        <HelpCircle className="h-3 w-3" />
        {t('usage.usageUnknown')}
      </Badge>
    );
  }

  const count = item.fallbackUsageCount ?? 0;
  const usedBy = item.usedBy ?? [];

  if (count === 0) {
    return (
      <Badge variant="outline" className="shrink-0 text-muted-foreground">
        {t('usage.notUsed')}
      </Badge>
    );
  }

  // Spec 352 §6: das Eintrags-Badge wird klickbar — Drill-down in die gefilterte
  // /services-Sicht. `&merchant=` wird mitgegeben, weil das Inventar den Merchant
  // kennt (verhindert cross-merchant-Zaehlung im Landungs-Banner).
  const drillHref = `/services?gw=${encodeURIComponent(item.iqServiceKey)}&merchant=${encodeURIComponent(item.merchantId)}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link to={drillHref} className="shrink-0">
          <Badge variant="secondary" className="cursor-pointer gap-1 font-medium hover:bg-secondary/80">
            <Link2 className="h-3 w-3" />
            {t('usage.inEntries', { count })}
          </Badge>
        </Link>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <p className="mb-1 font-medium">{t('usage.usedIn')}</p>
        <ul className="space-y-0.5">
          {usedBy.map((u) => (
            <li key={u.serviceId} className="text-xs">{u.displayName}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
